import { useState, useEffect } from "react";
import { Play, ChevronLeft, ChevronRight } from "lucide-react";
import {Button}  from "../custom_ui/button.tsx";
import {poster1} from "../../assets/";
import {poster2} from "../../assets/";
import { motion, AnimatePresence } from "framer-motion";

const defaultReleases = [
  {
    id: 1,
    image: poster1,
    title: "The Punudai",
    subtitle: "A Tale of Courage and Redemption",
    youtubeLink: "https://youtu.be/pQysG_I0i0k",
    category: "Feature Film",
    releaseDate: "2024",
  },
  {
    id: 2,
    image: poster2,
    title: "Punjabi Music Video",
    subtitle: "Soulful Melodies of Punjab",
    youtubeLink: "https://youtu.be/omsQAdouo-s",
    category: "Music Video",
    releaseDate: "2024",
  },
  {
    id: 3,
    image: poster2,
    title: "Romaanie",
    subtitle: "A Love Story Beyond Boundaries",
    youtubeLink: "https://youtu.be/EmWzgBzWhlA",
    category: "Romantic Drama",
    releaseDate: "2024",
  },
  {
    id: 4,
    image: poster2,
    title: "Pasus",
    subtitle: "Action-Packed Thriller",
    youtubeLink: "https://youtu.be/uMdpJieSTLk",
    category: "Action Thriller",
    releaseDate: "2023",
  },
  {
    id: 5,
    image: poster1,
    title: "Family Drama",
    subtitle: "Heartwarming Family Entertainment",
    youtubeLink: "https://youtu.be/Lx1CI3RUNGc",
    category: "Comedy Drama",
    releaseDate: "2023",
  },
  {
    id: 6,
    image: poster2,
    title: "Live Performance",
    subtitle: "Concert Spectacular",
    youtubeLink: "https://youtu.be/2dEBvAzh_Lo",
    category: "Concert",
    releaseDate: "2023",
  },
];

interface FeaturedReleasesSlideshowProps {
  releases?: any[];
}

export function FeaturedReleasesSlideshow({ releases }: FeaturedReleasesSlideshowProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const displayReleases = releases && releases.length > 0 ? releases.map(r => ({
      ...r,
      youtubeLink: r.youtube_link || r.youtubeLink,
      releaseDate: r.release_date || r.releaseDate,
      image: r.image || poster1 
  })) : defaultReleases;

  const currentRelease = displayReleases[currentIndex];

  useEffect(() => {
    if (!isAutoPlaying || isPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % displayReleases.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, isPlaying, displayReleases.length]);

  const goToPrevious = () => {
    setIsAutoPlaying(false);
    setIsPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + displayReleases.length) % displayReleases.length);
  };

  const goToNext = () => {
    setIsAutoPlaying(false);
    setIsPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % displayReleases.length);
  };

  const goToSlide = (index: number) => {
    setIsAutoPlaying(false);
    setIsPlaying(false);
    setCurrentIndex(index);
  };

  const getYoutubeId = (url: string) => {
    if (!url) return "";
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=))([^&?]+)/);
    return match ? match[1] : "";
  };

  return (
    <section className="py-20 bg-gradient-to-b from-background to-secondary/20" >
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <p className="text-primary font-medium tracking-[0.2em] uppercase mb-4">
            Featured Releases
          </p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-foreground">
            LATEST <span className="text-gradient-gold">PRODUCTIONS</span>
          </h2>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="relative">
            {/* Main Slide */}
            <div className="relative aspect-video rounded-xl overflow-hidden shadow-elevated group">
              <AnimatePresence mode="wait">
                {!isPlaying ? (
                  <motion.div
                    key={currentIndex}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.5, ease: "easeInOut" }}
                    className="absolute inset-0"
                  >
                    <img
                      src={currentRelease.image}
                      alt={currentRelease.title}
                      className="w-full h-full object-cover transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
                    
                    {/* Play Button */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <button
                        onClick={() => setIsPlaying(true)}
                        className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-gold flex items-center justify-center shadow-gold transition-all duration-300 hover:scale-110"
                      >
                        <Play
                          size={32}
                          className="text-primary-foreground ml-1"
                          fill="currentColor"
                        />
                      </button>
                    </div>

                    {/* Content */}
                    <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                      <span className="inline-block px-3 py-1 bg-primary/20 text-primary text-sm font-medium tracking-wider uppercase rounded-full mb-3">
                        {currentRelease.category} • {currentRelease.releaseDate}
                      </span>
                      <h3 className="font-display text-2xl md:text-4xl text-foreground mb-2">
                        {currentRelease.title}
                      </h3>
                      <p className="text-muted-foreground text-lg">
                        {currentRelease.subtitle}
                      </p>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div 
                    key="video"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0"
                  >
                    <iframe
                      src={`https://www.youtube.com/embed/${getYoutubeId(currentRelease.youtubeLink)}?autoplay=1`}
                      title={currentRelease.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Navigation Arrows */}
            <Button
              variant="outline"
              size="icon"
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-background/80 backdrop-blur-sm border-primary/30 hover:bg-primary hover:text-primary-foreground z-10"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-background/80 backdrop-blur-sm border-primary/30 hover:bg-primary hover:text-primary-foreground z-10"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>

          {/* Thumbnail Navigation */}
          <div className="flex justify-center gap-3 mt-6 overflow-x-auto pb-2">
            {displayReleases.map((release, index) => (
              <button
                key={release.id}
                onClick={() => goToSlide(index)}
                className={`relative flex-shrink-0 w-20 h-14 md:w-28 md:h-20 rounded-lg overflow-hidden transition-all duration-300 ${
                  index === currentIndex
                    ? "ring-2 ring-primary scale-105"
                    : "opacity-60 hover:opacity-100"
                }`}
              >
                <img
                  src={release.image}
                  alt={release.title}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>

          {/* Progress Dots */}
          <div className="flex justify-center gap-2 mt-4">
            {displayReleases.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? "w-8 bg-primary"
                    : "w-1.5 bg-muted-foreground/30 hover:bg-muted-foreground/50"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
